import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  pollSyncWorkflowProgress,
  fetchWorkflowStatus,
  clearSyncConfigWorkflow,
  clearActiveSyncWorkflow,
  fetchSyncObjects,
  fetchFields,
  fetchSyncSettings,
} from '../store/slices/analyticsSlice';
import { fetchDataSources } from '../store/slices/dataSourcesSlice';

/**
 * Non-blocking floating panel that shows sync progress in the bottom-right corner.
 * Users can minimize it or dismiss it while continuing to use the site.
 */
function SyncProgressPanel() {
  const dispatch = useDispatch();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Get both workflow types
  const syncConfigWorkflow = useSelector((state) => state.analytics.syncConfig.activeWorkflow);
  const fieldSyncWorkflow = useSelector((state) => state.analytics.activeSyncWorkflow);
  const selectedDataSource = useSelector((state) => state.dataSources.selectedDataSource);

  // Determine which workflow is active
  const activeWorkflow = syncConfigWorkflow || fieldSyncWorkflow;
  const workflowType = syncConfigWorkflow ? 'refresh' : 'sync';

  // Poll for progress
  useEffect(() => {
    // Check for both 'started' (initial trigger response) and 'running' (from Temporal)
    const isActive = activeWorkflow && (activeWorkflow.status === 'running' || activeWorkflow.status === 'started');
    if (!isActive) return;

    const interval = setInterval(() => {
      if (syncConfigWorkflow) {
        dispatch(pollSyncWorkflowProgress({ workflowId: activeWorkflow.workflow_id }));
      } else if (fieldSyncWorkflow) {
        dispatch(fetchWorkflowStatus({ workflowId: fieldSyncWorkflow.workflowId }));
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [dispatch, activeWorkflow, syncConfigWorkflow, fieldSyncWorkflow]);

  // Reset dismissed state when a new workflow starts
  useEffect(() => {
    if (activeWorkflow?.status === 'running' || activeWorkflow?.status === 'started') {
      setIsDismissed(false);
    }
  }, [activeWorkflow?.workflow_id, activeWorkflow?.workflowId]);

  // Auto-refresh all affected UI when workflow completes
  useEffect(() => {
    if (activeWorkflow?.status === 'completed') {
      const dataSourceId = selectedDataSource?.data_source_id || null;
      dispatch(fetchSyncObjects({ dataSourceId }));
      dispatch(fetchFields());
      dispatch(fetchDataSources());
      dispatch(fetchSyncSettings());
      // Auto-dismiss after a delay
      setTimeout(() => {
        handleDismiss();
      }, 5000);
    }
  }, [activeWorkflow?.status, dispatch]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (syncConfigWorkflow) {
      dispatch(clearSyncConfigWorkflow());
    }
    if (fieldSyncWorkflow) {
      dispatch(clearActiveSyncWorkflow());
    }
  };

  // Don't render if no active workflow or dismissed
  if (!activeWorkflow || isDismissed) return null;

  const {
    status,
    current_step,
    current_object,
    objects_processed,
    objects_total,
    records_processed,
    records_total,
    progress_percent,
    object_progress,
    error,
  } = activeWorkflow;

  const isRunning = status === 'running' || status === 'started';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  // Title based on workflow type and status
  const getTitle = () => {
    if (isCompleted) return workflowType === 'sync' ? 'Sync Complete' : 'Refresh Complete';
    if (isFailed) return workflowType === 'sync' ? 'Sync Failed' : 'Refresh Failed';
    return workflowType === 'sync' ? 'Syncing Data' : 'Refreshing Metadata';
  };

  // Status color
  const getStatusColor = () => {
    if (isCompleted) return 'bg-green-500';
    if (isFailed) return 'bg-red-500';
    return 'bg-blue-500';
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className={`${getStatusColor()} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 hover:opacity-90 transition`}
        >
          {isRunning && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {isCompleted && (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {isFailed && (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="text-sm font-medium">{getTitle()}</span>
          {isRunning && objects_total > 0 && (
            <span className="text-xs opacity-75">
              {objects_processed}/{objects_total}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`${getStatusColor()} px-4 py-2 flex items-center justify-between`}>
        <span className="text-white font-medium text-sm">{getTitle()}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="text-white/80 hover:text-white p-1"
            title="Minimize"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          {!isRunning && (
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white p-1"
              title="Dismiss"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isRunning && (
          <>
            {/* Current step */}
            <div className="text-sm text-gray-600 mb-2">
              {current_object ? (
                <span>Syncing <strong>{current_object}</strong></span>
              ) : (
                current_step || 'Processing...'
              )}
            </div>

            {/* Record count - primary metric */}
            {records_processed != null && records_processed > 0 && (
              <div className="mb-2">
                <span className="text-xl font-bold text-primary-600">
                  {records_processed.toLocaleString()}
                </span>
                {records_total > 0 && (
                  <span className="text-sm text-gray-500">
                    {' / '}{records_total.toLocaleString()}
                  </span>
                )}
                <span className="text-xs text-gray-400 ml-1">records</span>
              </div>
            )}

            {/* Object progress */}
            {objects_total > 0 && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Objects</span>
                  <span>{objects_processed} / {objects_total}</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${objects_total > 0 ? (objects_processed / objects_total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Per-object progress list */}
            {object_progress && object_progress.length > 0 && (
              <div className="mb-3 max-h-32 overflow-y-auto">
                <div className="text-xs font-medium text-gray-600 mb-1">Per-object status:</div>
                <div className="space-y-1">
                  {object_progress.map((obj) => (
                    <div key={obj.object_name} className="flex items-center justify-between text-xs">
                      <span className="truncate flex-1 mr-2">{obj.object_name}</span>
                      <div className="flex items-center gap-1">
                        {obj.status === 'pending' && (
                          <span className="text-gray-400">
                            {obj.records_total > 0 ? `${obj.records_total.toLocaleString()} records` : 'Pending'}
                          </span>
                        )}
                        {obj.status === 'syncing' && (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-primary-600"></div>
                            <span className="text-primary-600">
                              {obj.records_synced > 0
                                ? obj.records_total > 0
                                  ? `${obj.records_synced.toLocaleString()} / ${obj.records_total.toLocaleString()}`
                                  : obj.records_synced.toLocaleString()
                                : obj.records_total > 0
                                  ? `0 / ${obj.records_total.toLocaleString()}`
                                  : 'Syncing...'}
                            </span>
                          </>
                        )}
                        {obj.status === 'completed' && (
                          <>
                            <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-green-600">
                              {obj.records_synced.toLocaleString()}
                              {obj.records_total > 0 && obj.records_synced !== obj.records_total && (
                                <span className="text-gray-400 ml-1">/ {obj.records_total.toLocaleString()}</span>
                              )}
                            </span>
                          </>
                        )}
                        {obj.status === 'failed' && (
                          <>
                            <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="text-red-600">Failed</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Spinner - only show if no per-object progress */}
            {(!object_progress || object_progress.length === 0) && (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            )}
          </>
        )}

        {isCompleted && (
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            {records_processed > 0 && (
              <p className="text-lg font-bold text-gray-800">
                {records_processed.toLocaleString()} records
              </p>
            )}
            <p className="text-sm text-gray-500">
              {objects_processed > 0 ? `${objects_processed} objects processed` : 'Complete'}
            </p>
          </div>
        )}

        {isFailed && (
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm text-red-600">{error || 'An error occurred'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SyncProgressPanel;
